/**
 * UIStateService - Gestisce la persistenza dello stato dell'UI in localStorage
 *
 * Questo servizio salva e ripristina:
 * - Route corrente
 * - Active file ID
 * - Posizione di scroll
 * - Stato dei filtri e della visualizzazione
 *
 * Questo previene il reload completo quando l'utente esce e rientra nella piattaforma
 */

export interface UIState {
  // Routing
  currentPath: string;
  searchParams?: string;

  // File Management
  activeFileId: string | null;

  // Scroll Position
  scrollPosition: number;

  // View State
  selectedFilters?: Record<string, any>;
  viewMode?: string;

  // Timestamp
  lastUpdated: number;
}

const UI_STATE_KEY = 'tradingspace_ui_state';
const STATE_EXPIRY_TIME = 24 * 60 * 60 * 1000; // 24 hours

export class UIStateService {
  private static currentState: UIState | null = null;

  /**
   * Salva lo stato corrente dell'UI in localStorage
   */
  static saveState(partialState: Partial<UIState>, userId?: string): void {
    try {
      const storageKey = userId ? `${UI_STATE_KEY}_${userId}` : UI_STATE_KEY;

      // Merge con lo stato esistente
      const existingState = this.loadState(userId);
      const newState: UIState = {
        ...existingState,
        ...partialState,
        lastUpdated: Date.now()
      };

      localStorage.setItem(storageKey, JSON.stringify(newState));
      this.currentState = newState;

      console.log('💾 UI State salvato:', {
        path: newState.currentPath,
        activeFile: newState.activeFileId,
        scroll: newState.scrollPosition
      });
    } catch (error) {
      console.error('❌ Errore salvataggio UI state:', error);
    }
  }

  /**
   * Carica lo stato salvato da localStorage
   */
  static loadState(userId?: string): UIState {
    try {
      const storageKey = userId ? `${UI_STATE_KEY}_${userId}` : UI_STATE_KEY;
      const stored = localStorage.getItem(storageKey);

      if (!stored) {
        return this.getDefaultState();
      }

      const state: UIState = JSON.parse(stored);

      // Verifica se lo stato è scaduto
      const isExpired = Date.now() - state.lastUpdated > STATE_EXPIRY_TIME;
      if (isExpired) {
        console.log('⚠️ UI State scaduto, uso default');
        return this.getDefaultState();
      }

      this.currentState = state;
      console.log('✅ UI State caricato:', {
        path: state.currentPath,
        activeFile: state.activeFileId,
        age: `${Math.round((Date.now() - state.lastUpdated) / 1000 / 60)}min`
      });

      return state;
    } catch (error) {
      console.error('❌ Errore caricamento UI state:', error);
      return this.getDefaultState();
    }
  }

  /**
   * Aggiorna solo il path corrente
   */
  static updatePath(path: string, searchParams?: string, userId?: string): void {
    this.saveState({
      currentPath: path,
      searchParams
    }, userId);
  }

  /**
   * Aggiorna solo l'active file
   */
  static updateActiveFile(fileId: string | null, userId?: string): void {
    this.saveState({ activeFileId: fileId }, userId);
  }

  /**
   * Aggiorna solo la posizione di scroll
   */
  static updateScrollPosition(position: number, userId?: string): void {
    this.saveState({ scrollPosition: position }, userId);
  }

  /**
   * Aggiorna i filtri
   */
  static updateFilters(filters: Record<string, any>, userId?: string): void {
    this.saveState({ selectedFilters: filters }, userId);
  }

  /**
   * Ripristina la posizione di scroll salvata
   */
  static restoreScrollPosition(userId?: string): void {
    const state = this.loadState(userId);
    if (state.scrollPosition > 0) {
      setTimeout(() => {
        window.scrollTo({
          top: state.scrollPosition,
          behavior: 'smooth'
        });
        console.log(`📜 Scroll ripristinato a ${state.scrollPosition}px`);
      }, 100);
    }
  }

  /**
   * Ottiene lo stato corrente in memoria
   */
  static getCurrentState(): UIState | null {
    return this.currentState;
  }

  /**
   * Pulisce lo stato salvato
   */
  static clearState(userId?: string): void {
    try {
      const storageKey = userId ? `${UI_STATE_KEY}_${userId}` : UI_STATE_KEY;
      localStorage.removeItem(storageKey);
      this.currentState = null;
      console.log('🗑️ UI State pulito');
    } catch (error) {
      console.error('❌ Errore pulizia UI state:', error);
    }
  }

  /**
   * Ritorna lo stato di default
   */
  private static getDefaultState(): UIState {
    return {
      currentPath: '/',
      activeFileId: null,
      scrollPosition: 0,
      lastUpdated: Date.now()
    };
  }

  /**
   * Inizializza il tracking automatico dello scroll
   */
  static initScrollTracking(userId?: string): () => void {
    let scrollTimeout: NodeJS.Timeout;

    const handleScroll = () => {
      // Debounce per evitare troppi salvataggi
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        const scrollY = window.scrollY;
        if (scrollY > 50) { // Salva solo se ha scrollato più di 50px
          this.updateScrollPosition(scrollY, userId);
        }
      }, 500);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    // Cleanup function
    return () => {
      clearTimeout(scrollTimeout);
      window.removeEventListener('scroll', handleScroll);
    };
  }

  /**
   * Inizializza il tracking automatico della route
   */
  static initRouteTracking(userId?: string): void {
    // Salva lo stato iniziale
    const currentPath = window.location.pathname;
    const searchParams = window.location.search;
    this.updatePath(currentPath, searchParams, userId);

    console.log('🚀 Route tracking inizializzato:', currentPath);
  }
}

export default UIStateService;
